import { useEffect } from 'react';

type UseDoubleClickProps = {
  ref: any;
  latency?: number;
  onSingleClick?: () => void;
  onDoubleClick?: () => void;
};

/**
 * Custom hook to handle single and double click events on a referenced element.
 * @function useDoubleClick
 * @param {UseDoubleClickProps} props - The properties for the hook.
 * @param {React.RefObject<HTMLElement>} props.ref - The reference to the element to attach the click events.
 * @param {number} [props.latency=300] - The latency in milliseconds to differentiate between single and double clicks.
 * @param {() => void} [props.onSingleClick] - The callback function to be called on a single click.
 * @param {() => void} [props.onDoubleClick] - The callback function to be called on a double click.
 */
export const useDoubleClick = ({
  ref,
  latency = 300,
  onSingleClick = () => null,
  onDoubleClick = () => null,
}: UseDoubleClickProps) => {
  useEffect(() => {
    const clickRef = ref.current;
    let clickCount = 0;

    const handleClick = () => {
      clickCount += 1;

      setTimeout(() => {
        if (clickCount === 1) onSingleClick();
        else if (clickCount === 2) onDoubleClick();

        clickCount = 0;
      }, latency);
    };

    // Add event listener for click events
    clickRef.addEventListener('click', handleClick);

    // Remove event listener
    return () => {
      clickRef.removeEventListener('click', handleClick);
    };
  }, [ref, latency, onSingleClick, onDoubleClick]);
};
